import { useState, useEffect } from 'react'
import { SERVICES, svcDisplayColor } from '../constants.js'
import { safeLinkHref } from '../security.js'
import { hapticLight, hapticMedium, shareTitle, canShare } from '../native.js'
import Badge from './Badge.jsx'
import { track } from '../analytics.js'

const VPN_URL   = import.meta.env.VITE_VPN_AFFILIATE_URL || ''
const VPN_BRAND = import.meta.env.VITE_VPN_BRAND || 'NordVPN'

const ACTION_LABEL = { flatrate: 'Watch on', free: 'Watch free on', ads: 'Watch on', rent: 'Rent on', buy: 'Buy on' }

const isMobile = () => typeof navigator !== 'undefined'
  && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

function tryOpenApp(appUrl, webUrl) {
  // Custom URI scheme only (nflx://, hulu:///, peacock:///, paramountplus:///, …).
  // Universal Links (https://) are NOT routed here — callers let the native
  // <a href target="_blank"> handle those because iOS/Android fires Universal Link
  // routing automatically on any tab-opening navigation; no interception needed.

  // Fire the scheme directly via window.location.href — this MUST run
  // synchronously inside the user-gesture context so iOS/Android honours it.
  // (iframes can no longer open custom schemes on iOS 14+ / modern Android.)
  window.location.href = appUrl

  // If the app opened the page loses visibility → cancel the fallback so we
  // don't navigate away when the user returns to the browser tab.
  // If the app is NOT installed the scheme fails silently and the page stays
  // visible → after the timeout redirect to the web URL.
  const timer = setTimeout(() => {
    window.location.href = webUrl
  }, 1500)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') clearTimeout(timer)
  }, { once: true })
}

// Sub-label shown beneath a streaming button to communicate access level
function AccessLabel({ type }) {
  if (type === 'ads')  return <span style={{ fontSize: 9, opacity: 0.72, marginTop: 2 }}>free with ads</span>
  if (type === 'free') return <span style={{ fontSize: 9, opacity: 0.72, marginTop: 2, color: '#4ade80' }}>free tier</span>
  return null
}

// Muted "Also on" chip — grayscale at rest, full colour on hover
function OtherServiceChip({ svc, rec, directUrls, bgMode }) {
  const [hov, setHov] = useState(false)
  const s = SERVICES.find(x => x.id === svc.service)
  if (!s) return null
  const url = safeLinkHref(directUrls?.[s.id]) || s.link(rec.title)
  const label = svc.type === 'ads' ? `${s.short} (ads)` : s.short
  return (
    <a
      href={url} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      title={`Watch on ${s.short}${svc.type === 'ads' ? ' (with ads)' : ''} — subscription needed`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '4px 8px', borderRadius: 7,
        border: `1px solid ${hov ? s.color + '55' : 'rgba(255,255,255,0.09)'}`,
        background: hov ? s.color + '18' : 'rgba(255,255,255,0.04)',
        filter: hov ? 'none' : 'grayscale(0.75) opacity(0.55)',
        color: hov ? svcDisplayColor(s, bgMode) : 'rgba(200,200,200,0.75)',
        fontSize: 10, fontWeight: 700, textDecoration: 'none',
        cursor: 'pointer', whiteSpace: 'nowrap',
        transition: 'all 0.18s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
      onClick={() => track('Portal_Clickthrough', {
        service_id: s.id, service_name: s.short,
        title: rec.title, tmdb_id: rec.tmdbId || null,
        watch_type: svc.type, is_paid: false,
      })}
    >
      {svc.type === 'ads' ? '📺' : '▶'} {label}
    </a>
  )
}

// Multi-service 4-tier layout — used when backend returns the new tier arrays
function MultiServiceWatch({ watch, rec, fallbackService, bgMode }) {
  const [launchingId, setLaunchingId] = useState(null)

  const directUrls = watch.directUrls || {}
  const appUrls    = watch.appUrls    || {}
  const userSvcs   = watch.userServices    || []
  const otherSvcs  = watch.otherServices   || []
  const buySvcs    = watch.purchaseServices || []

  // If user has no matching subscriptions, surface other services as prominent (muted) buttons
  const showFallbackOther = userSvcs.length === 0 && otherSvcs.length > 0

  const launch = (svc, e) => {
    const s = SERVICES.find(x => x.id === svc.service)
    if (!s || launchingId === s.id) return
    const isPaid    = svc.type === 'rent' || svc.type === 'buy'
    const directUrl = safeLinkHref(directUrls[s.id])
    const appUrl    = safeLinkHref(appUrls[s.id])
    const href      = directUrl || s.link(rec.title)

    // Diagnostic log — remove once links are confirmed stable
    console.log('[Flow] Watch tapped:', s.name, '| url:', href, '| app:', appUrl || 'none')

    track('Portal_Clickthrough', {
      service_id: s.id, service_name: s.short,
      title: rec.title, tmdb_id: rec.tmdbId || null,
      year: rec.year || null, genres: rec.genres || [],
      watch_type: svc.type, is_paid: isPaid,
    })
    setLaunchingId(s.id)
    setTimeout(() => setLaunchingId(null), 700)
  }

  // NOTE: Subscription buttons are rendered as direct <a> elements below rather
  // than through a PrimaryBtn sub-component.  Defining a component function
  // inside MultiServiceWatch creates a new function reference on every render;
  // React then treats it as a different component type, unmounts the old <a>
  // node, and remounts a fresh one — which causes mobile Safari to cancel the
  // in-flight navigation before it can open the link.  Inline rendering (same
  // pattern as the Rent/Buy section) avoids the unmount entirely.

  return (
    <div style={{ marginBottom: 2 }}>
      {/* ── Tier 1: user's subscribed services ─────────────── */}
      {userSvcs.map(svc => {
        const s = SERVICES.find(x => x.id === svc.service)
        if (!s) return null
        const isLaunching = launchingId === s.id
        const isPaid = svc.type === 'rent' || svc.type === 'buy'
        const isAds  = svc.type === 'ads'
        const isFree = svc.type === 'free'
        const verb   = isPaid ? (svc.type === 'rent' ? 'Rent on' : 'Buy on')
                     : (isAds || isFree) ? 'Watch free on' : 'Watch on'
        const href   = safeLinkHref(directUrls[s.id]) || s.link(rec.title)
        return (
          <a
            key={svc.service}
            href={href}
            target="_blank"
            rel={s.affiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
            onClick={(e) => launch(svc, e)}
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              width: '100%', boxSizing: 'border-box',
              background: isLaunching
                ? (isPaid ? 'linear-gradient(135deg,rgba(251,191,36,0.42),rgba(180,120,20,0.30))' : `linear-gradient(135deg,${s.color}50,${s.color}32)`)
                : (isPaid ? 'linear-gradient(135deg,rgba(251,191,36,0.22),rgba(180,120,20,0.15))' : `linear-gradient(135deg,${s.color}28,${s.color}14)`),
              border: isLaunching
                ? `1.5px solid ${isPaid ? 'rgba(251,191,36,0.85)' : s.color + 'aa'}`
                : `1.5px solid ${isPaid ? '#fbbf24' : s.color + '66'}`,
              color: isPaid ? '#d97706' : svcDisplayColor(s, bgMode),
              borderRadius: 12, padding: '10px 6px',
              fontSize: 12, fontWeight: 700, cursor: isLaunching ? 'default' : 'pointer',
              fontFamily: 'inherit', marginBottom: 5, minHeight: 44,
              textDecoration: 'none', textAlign: 'center', lineHeight: 1.25,
              WebkitTapHighlightColor: 'transparent',
              transform: isLaunching ? 'scale(1.028)' : 'scale(1)',
              boxShadow: isLaunching
                ? `0 0 0 3px ${isPaid ? 'rgba(251,191,36,0.22)' : s.color + '28'}, 0 8px 28px ${isPaid ? 'rgba(251,191,36,0.14)' : s.color + '18'}`
                : 'none',
              transition: 'transform 0.22s cubic-bezier(0.22,0.68,0,1.15), box-shadow 0.22s ease, background 0.18s ease, border-color 0.18s ease',
            }}
          >
            <span>{isLaunching ? '✦ Opening…' : `${isPaid ? '💳' : '▶'} ${verb} ${s.short}`}</span>
            {!isLaunching && <AccessLabel type={svc.type} />}
          </a>
        )
      })}

      {/* ── Tier 1 fallback: no match — show other services ─ */}
      {showFallbackOther && (
        <>
          <div style={{ fontSize: 9, color: 'rgba(200,200,200,0.45)', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>
            NOT IN YOUR SUBSCRIPTIONS — ALSO ON
          </div>
          {otherSvcs.slice(0, 3).map(svc => {
            const s = SERVICES.find(x => x.id === svc.service)
            if (!s) return null
            return (
              <a key={svc.service}
                href={safeLinkHref(directUrls[s.id]) || s.link(rec.title)}
                target="_blank" rel="noopener noreferrer"
                onClick={(e) => launch(svc, e)}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  width: '100%', boxSizing: 'border-box',
                  background: `linear-gradient(135deg,${s.color}18,${s.color}0c)`,
                  border: `1px solid ${s.color}40`,
                  color: svcDisplayColor(s, bgMode), opacity: 0.78,
                  borderRadius: 12, padding: '9px 6px',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', marginBottom: 4, minHeight: 40,
                  textDecoration: 'none', textAlign: 'center', lineHeight: 1.25,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span>▶ Watch on {s.short}</span>
                <span style={{ fontSize: 9, opacity: 0.7, marginTop: 2 }}>
                  {svc.type === 'ads' ? 'free with ads' : svc.type === 'free' ? 'free tier' : 'subscription needed'}
                </span>
              </a>
            )
          })}
        </>
      )}

      {/* ── Tier 2: user has matches AND other services exist ── */}
      {userSvcs.length > 0 && otherSvcs.length > 0 && (
        <div style={{ marginBottom: 6, marginTop: 1 }}>
          <div style={{ fontSize: 9, color: 'rgba(200,200,200,0.4)', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 5 }}>
            ALSO STREAMING ON
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {otherSvcs.slice(0, 5).map(svc => (
              <OtherServiceChip key={svc.service} svc={svc} rec={rec} directUrls={directUrls} bgMode={bgMode} />
            ))}
          </div>
        </div>
      )}

      {/* ── Tier 3: rent / purchase ──────────────────────── */}
      {buySvcs.length > 0 && (
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.07)',
          paddingTop: 7, marginTop: 3, marginBottom: 5,
        }}>
          <div style={{ fontSize: 9, color: 'rgba(200,200,200,0.38)', fontWeight: 700, letterSpacing: '0.05em', marginBottom: 5 }}>
            RENT OR PURCHASE
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {buySvcs.slice(0, 5).map(svc => {
              const s = SERVICES.find(x => x.id === svc.service)
              if (!s) return null
              const url = safeLinkHref(directUrls[s.id]) || s.link(rec.title)
              return (
                <a key={`${svc.service}-${svc.type}`} href={url} target="_blank"
                  rel={s.affiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
                  onClick={(e) => launch(svc, e)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '4px 8px', borderRadius: 7,
                    border: '1px solid rgba(251,191,36,0.22)',
                    background: 'rgba(251,191,36,0.06)',
                    color: '#b45309', fontSize: 10, fontWeight: 600,
                    textDecoration: 'none', cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {svc.type === 'rent' ? '🎬' : '💳'} {svc.type} · {s.short}
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function WatchAction({ rec, fallbackService, bgMode = 'dark' }) {
  const [launching, setLaunching]   = useState(false)

  const watch       = rec.watch || null
  const regionLocked = !!watch?.regionLocked

  // ── Region-locked renders ────────────────────────────
  if (regionLocked && VPN_URL) {
    const where = (watch.availableIn || []).slice(0, 3).join(', ')
    return (
      <a
        href={VPN_URL} target="_blank" rel="sponsored noopener noreferrer"
        style={{
          width: '100%', boxSizing: 'border-box',
          background: 'linear-gradient(135deg,rgba(34,197,94,0.18),rgba(20,80,40,0.22))',
          border: '1.5px solid rgba(34,197,94,0.35)', color: '#4ade80',
          borderRadius: 12, padding: '10px 8px',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'inherit', marginBottom: 7, minHeight: 44,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textDecoration: 'none', textAlign: 'center', lineHeight: 1.3,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span>🌍 Unlock with {VPN_BRAND}</span>
        {where && <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>Streams in {where}</span>}
      </a>
    )
  }

  if (regionLocked) {
    return (
      <div style={{
        width: '100%', boxSizing: 'border-box',
        background: 'rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.1)', color: '#6b7280',
        borderRadius: 12, padding: '10px 8px',
        fontSize: 11, fontWeight: 600, marginBottom: 7, minHeight: 44,
        display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      }}>Not in your region</div>
    )
  }

  // ── New 4-tier layout (backend v2) ───────────────────
  if (watch && Array.isArray(watch.userServices)) {
    return <MultiServiceWatch watch={watch} rec={rec} fallbackService={fallbackService} bgMode={bgMode} />
  }

  // ── Legacy single-service layout (backward compat) ──
  const primaryServiceId = watch?.primary?.service || fallbackService.id
  const type    = watch?.primary?.type || 'flatrate'
  const s       = SERVICES.find(x => x.id === primaryServiceId) || fallbackService
  const verb    = ACTION_LABEL[type] || 'Watch on'
  const isPaid  = type === 'rent' || type === 'buy'
  const notSubbed = !!watch?.primary?.notSubscribed

  const directUrls = watch?.directUrls || {}
  const directUrl  = safeLinkHref(directUrls[s.id])
  const appUrl     = safeLinkHref(watch?.appUrls?.[s.id])
  const watchHref = directUrl || s.link(rec.title)

  const handleLaunch = (e) => {
    if (launching) return

    // Never intercept — same reasoning as MultiServiceWatch.launch().
    // The native <a href target="_blank"> with an https:// URL triggers
    // OS-level Universal Link routing for any installed app automatically.

    track('Portal_Clickthrough', {
      service_id: s.id, service_name: s.short,
      title: rec.title, tmdb_id: rec.tmdbId || null,
      year: rec.year || null, genres: rec.genres || [],
      watch_type: type, is_paid: isPaid,
    })
    setLaunching(true)
    setTimeout(() => setLaunching(false), 700)
  }

  return (
    <>
      <a
        href={watchHref} target="_blank"
        rel={s.affiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
        onClick={handleLaunch}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: launching
            ? (isPaid ? 'linear-gradient(135deg,rgba(251,191,36,0.42),rgba(180,120,20,0.30))' : `linear-gradient(135deg,${s.color}50,${s.color}32)`)
            : (isPaid ? 'linear-gradient(135deg,rgba(251,191,36,0.22),rgba(180,120,20,0.15))' : `linear-gradient(135deg,${s.color}28,${s.color}14)`),
          border: launching
            ? `1.5px solid ${isPaid ? 'rgba(251,191,36,0.85)' : s.color + 'aa'}`
            : `1.5px solid ${isPaid ? '#fbbf24' : s.color + '66'}`,
          color: isPaid ? '#d97706' : svcDisplayColor(s, bgMode),
          borderRadius: 12, padding: '10px 6px',
          fontSize: 12, fontWeight: 700, cursor: launching ? 'default' : 'pointer',
          fontFamily: 'inherit', marginBottom: notSubbed || watch?.alt ? 5 : 7, minHeight: 44,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textDecoration: 'none', textAlign: 'center', lineHeight: 1.25,
          WebkitTapHighlightColor: 'transparent',
          transform: launching ? 'scale(1.028)' : 'scale(1)',
          boxShadow: launching
            ? `0 0 0 3px ${isPaid ? 'rgba(251,191,36,0.22)' : s.color + '28'}, 0 8px 28px ${isPaid ? 'rgba(251,191,36,0.14)' : s.color + '18'}`
            : 'none',
          transition: 'transform 0.22s cubic-bezier(0.22,0.68,0,1.15), box-shadow 0.22s ease, background 0.18s ease, border-color 0.18s ease',
        }}
      >
        <span>{launching ? '✦ Opening…' : `${isPaid ? '💳' : '▶'} ${verb} ${s.short}`}</span>
        {!launching && notSubbed && <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.85, marginTop: 2 }}>Subscription needed</span>}
      </a>
      {watch?.alt && (() => {
        const altS = SERVICES.find(x => x.id === watch.alt.service)
        if (!altS) return null
        return (
          <a href={safeLinkHref(directUrls[altS.id]) || altS.link(rec.title)} target="_blank"
            rel={altS.affiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              fontSize: 10, color: '#16a34a', textDecoration: 'none',
              padding: '4px 0', marginBottom: 7, lineHeight: 1.3, textAlign: 'center', fontWeight: 600,
            }}
          >
            💡 Free on {altS.short} with subscription
          </a>
        )
      })()}
    </>
  )
}

function Stars({ n }) {
  if (!n) return null
  const c = parseFloat(n) >= 7.5 ? '#10b981' : parseFloat(n) >= 6 ? '#d97706' : '#ef4444'
  return <span style={{ fontSize: 11, color: c, fontWeight: 700 }}>★ {n}</span>
}

export default function Card({ rec, idx, prefs, onRate, onMoreLike, onTrailer, bgMode = 'dark' }) {
  const [vis, setVis]               = useState(false)
  const [imgOk, setImgOk]           = useState(true)
  const [pressed, setPressed]       = useState(false)
  const [hovered, setHovered]       = useState(false)
  const [descExpanded, setDescExpanded] = useState(false)
  const s       = SERVICES.find(x => x.id === rec.service) || SERVICES[0]
  const existing = prefs.find(p => p.title === rec.title)

  useEffect(() => {
    const t = setTimeout(() => setVis(true), idx * 65)
    return () => clearTimeout(t)
  }, [idx])

  const transform = !vis
    ? 'translateY(22px)'
    : pressed
      ? 'scale(0.97)'
      : hovered
        ? 'translateY(-6px) scale(1.015)'
        : 'translateY(0) scale(1)'

  const shadow = hovered && !pressed
    ? '0 20px 56px rgba(4,14,7,0.32), 0 0 0 1.5px rgba(34,197,94,0.35), 0 0 28px rgba(34,197,94,0.10)'
    : '0 8px 32px rgba(4,14,7,0.15)'

  const border = hovered && !pressed
    ? '1.5px solid rgba(34,197,94,0.4)'
    : '1px solid rgba(34,197,94,0.15)'

  return (
    /* article gives the card a meaningful landmark role. aria-label names
       it so screen readers announce "Article: <title>" when navigating by
       landmark, satisfying WCAG 1.3.1 and 4.1.2. */
    <article
      aria-label={rec.title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false) }}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        opacity: vis ? 1 : 0,
        transform,
        transition: 'opacity 0.38s ease, transform 0.28s cubic-bezier(.22,.68,0,1.15), box-shadow 0.28s ease, border-color 0.2s ease',
        borderRadius: 18, overflow: 'hidden',
        background: 'rgba(255,255,255,0.08)',
        border,
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        display: 'flex', flexDirection: 'column',
        boxShadow: shadow,
        WebkitTapHighlightColor: 'transparent',
        cursor: 'default',
      }}
    >
      {/* Poster - Interactive Area.
           Replaced <div onClick> with <button> to satisfy WCAG 4.1.2 (Name,
           Role, Value) and 2.1.1 (Keyboard). A bare div is not reachable via
           Tab and has no implicit role, making it invisible to AT.
           paddingTop:148% is the aspect-ratio trick for CLS control (WCAG 1.4.4). */}
      <button
        type="button"
        onClick={() => onTrailer(rec)}
        aria-label={`${rec.trailer ? 'Watch trailer for' : 'View details for'} ${rec.title}${rec.year ? ` (${rec.year})` : ''}`}
        style={{
          position: 'relative',
          paddingTop: '148%',
          paddingLeft: 0, paddingRight: 0, paddingBottom: 0,
          background: 'rgba(4,14,7,0.2)',
          overflow: 'hidden',
          flexShrink: 0,
          cursor: 'pointer',
          display: 'block',
          width: '100%',
          border: 'none',
          margin: 0,
        }}
      >
        {rec.poster && imgOk ? (
          /* Descriptive alt text satisfies WCAG 1.1.1 (Non-text Content).
             Generic alt={title} fails because a title alone provides no
             context about image type or content. */
          <img
            src={rec.poster}
            alt={`Official promotional poster for ${rec.title}${rec.year ? ` (${rec.year})` : ''}`}
            onError={() => setImgOk(false)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(160deg,${s.color}18,rgba(0,0,0,0.05))`,
            gap: 8, padding: 16,
          }}>
            <span style={{ fontSize: 40 }}>🎬</span>
            <span style={{ fontSize: 12, color: 'inherit', textAlign: 'center', lineHeight: 1.4, fontWeight: 600 }}>{rec.title}</span>
          </div>
        )}

        {/* Shadow Vignette */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* aria-hidden: service badge is decorative — the service name is
             conveyed by the WatchAction button label below the card. */}
        <div aria-hidden="true" style={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
          <Badge id={rec.service} bgMode={bgMode} />
        </div>

        {(rec.tmdb || rec.rating) && (
          <div style={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            background: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '3px 8px',
            backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Stars n={rec.tmdb || rec.rating} />
          </div>
        )}

        {rec.versionBadge && (() => {
          const cfg = rec.versionBadge === 'REMAKE'
            ? { bg: 'rgba(56,140,230,0.9)', border: 'rgba(100,180,255,0.6)', label: '✦ REMAKE' }
            : rec.versionBadge === 'ORIGINAL'
            ? { bg: 'rgba(217,119,6,0.9)',  border: 'rgba(251,191,36,0.6)',  label: '★ ORIGINAL' }
            : { bg: 'rgba(124,58,237,0.9)',  border: 'rgba(167,139,250,0.6)', label: '◆ CLASSIC' }
          return (
            <div style={{
              position: 'absolute', bottom: 12, left: 10, zIndex: 3,
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderRadius: 6, padding: '3px 7px',
              fontSize: 9, fontWeight: 800, color: '#fff',
              letterSpacing: '0.06em', backdropFilter: 'blur(6px)',
            }}>{cfg.label}</div>
          )
        })()}

        {/* aria-hidden: purely decorative affordance — the parent button
             already carries the accessible name and handles the action. */}
        {rec.trailer && (
          <div aria-hidden="true" style={{
            position: 'absolute', bottom: 12, right: 12, zIndex: 3,
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 50, width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', fontSize: 12, color: '#1e293b',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>▶</div>
        )}
      </button>

      {/* Details Area */}
      <div style={{ padding: '13px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: 'inherit', marginBottom: 5, lineHeight: 1.3, letterSpacing: '-0.1px',
        }}>{rec.title}</div>

        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 7, alignItems: 'center' }}>
          {rec.year && <span style={{ fontSize: 10, color: 'inherit', opacity: 0.75, fontWeight: 600 }}>{rec.year}</span>}
          {rec.genres?.slice(0, 2).map(g => (
            <span key={g} style={{
              fontSize: 10, color: 'inherit', opacity: 0.8,
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.12)',
              borderRadius: 4, padding: '1px 5px', fontWeight: 500
            }}>{g}</span>
          ))}
        </div>

        {(() => {
          const text = rec.overview || rec.reason || 'A great pick for tonight.'
          const isLong = text.length > 115
          return (
            <>
              <p style={{
                fontSize: 12, color: 'inherit', opacity: 0.85, lineHeight: 1.5,
                margin: '0 0 6px',
                ...(isLong && !descExpanded ? {
                  display: '-webkit-box', WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                } : {}),
              }}>
                {text}
              </p>

              {isLong && (
                /* aria-expanded tells AT whether the full description is visible,
                 satisfying WCAG 4.1.2 for disclosure controls. */
              <button
                  aria-expanded={descExpanded}
                  onClick={(e) => { e.stopPropagation(); setDescExpanded(x => !x); }}
                  style={{
                    background: 'none', border: 'none', padding: '0 0 10px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
                    color: '#16a34a', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  <span
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 14, height: 14, borderRadius: '50%',
                      border: '1.5px solid rgba(22,163,74,0.35)',
                      fontSize: 8, flexShrink: 0,
                      transform: descExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
                    }}
                  >▾</span>
                  {descExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </>
          )
        })()}

        {/* Action Controls */}
        <div style={{ marginTop: 'auto' }}>
          <WatchAction rec={rec} fallbackService={s} bgMode={bgMode} />

          <button onClick={() => { hapticMedium(); onMoreLike(rec) }} style={{
            width: '100%', background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.15)', color: '#16a34a',
            borderRadius: 10, padding: '8px 0',
            fontSize: 11, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', marginBottom: 5, minHeight: 38,
            WebkitTapHighlightColor: 'transparent',
          }}>🔥 More like this</button>

          {canShare({ title: rec.title }) && (
            <button
              onClick={async (e) => {
                e.stopPropagation()
                hapticLight()
                const shared = await shareTitle(rec)
                if (shared) track('Share_Title', { title: rec.title, service: rec.service })
              }}
              style={{
                width: '100%', background: 'rgba(34,197,94,0.06)',
                border: '1px solid rgba(34,197,94,0.15)', color: '#16a34a',
                borderRadius: 10, padding: '8px 0',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', marginBottom: 9, minHeight: 38,
                WebkitTapHighlightColor: 'transparent',
              }}
            >↑ Share</button>
          )}

          <div style={{ display: 'flex', gap: 5 }}>
            {/* aria-label gives emoji-only buttons an accessible name (WCAG 4.1.2).
                aria-pressed communicates toggle state to AT (WCAG 4.1.2). */}
            {[
              ['like',    '👍', 'Like'],
              ['dislike', '👎', 'Dislike'],
              ['watched', '✅', 'Mark as watched'],
            ].map(([type, icon, label]) => (
              <button
                key={type}
                aria-label={label}
                aria-pressed={existing?.type === type}
                onClick={() => { hapticLight(); onRate(rec, type) }}
                style={{
                  flex: 1,
                  background: existing?.type === type ? 'rgba(22,163,74,0.18)' : 'rgba(0,0,0,0.03)',
                  border: `1px solid ${existing?.type === type ? 'rgba(22,163,74,0.4)' : 'rgba(0,0,0,0.08)'}`,
                  color: existing?.type === type ? '#16a34a' : 'inherit',
                  borderRadius: 8, padding: '7px 0',
                  fontSize: 14, cursor: 'pointer', minHeight: 38,
                  fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
                  opacity: existing?.type && existing.type !== type ? 0.4 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    </article>
  )
}